import {useMediaQuery} from '@mui/material'
import React, {useEffect, useMemo} from 'react'
import {useDispatch} from 'react-redux'
import {FeatureBlock, FeatureItem} from 'entities/credits'
import {
  GetPackages,
  PackageType,
  setConfirmPayModal,
  setPackages,
  usePaymentPackages
} from 'entities/payment'
import {getDiscount, getPerCoinValue, names} from 'shared/lib'
import {AdvantageItem} from 'shared/ui/advantageItem'
import {Button} from 'shared/ui/buttons'
import {HorizontalDivider} from 'shared/ui/dividers'
import {
  GoCoin20,
  Headset,
  Heart,
  HistoryCleaning,
  Incognito,
  MessageImage,
  Messages,
  Upload
} from 'shared/ui/icons'
import styles from './styles.module.scss'

export const Packages = () => {
  const dispatch = useDispatch()
  const packages = usePaymentPackages()

  const onClickHandler = (openPackage: PackageType) => {
    dispatch(setConfirmPayModal({...openPackage, isOpen: true}))
  }

  useEffect(() => {
    GetPackages()
      .then(({data}) => dispatch(setPackages(data.data)))
      .catch(console.error)
  }, [])

  const PackageCardComponent = useMemo(
    () => (packages.length > 1 ? PackageCard : PackageFirstCard),
    [packages]
  )

  return (
    <div
      className={names(styles.wrapper, packages.length > 1 ? '' : styles.first)}
    >
      {packages.map((item) => (
        <PackageCardComponent
          {...item}
          key={item.id}
          onClickHandler={onClickHandler}
        />
      ))}
    </div>
  )
}

type PackageCardProps = PackageType & {
  onClickHandler: (openPackage: PackageType) => void
}

const PackageCard: React.FC<PackageCardProps> = ({
  id,
  name,
  credits,
  stickers,
  photos,
  videos,
  virtualGifts,
  price,
  originPrice,
  onClickHandler
}) => {
  const withSale = originPrice && originPrice !== price

  return (
    <div className={styles.card}>
      <div className={styles.info}>
        <div className={styles.info__header}>
          <h5 className={styles.name}>
            {name}
            {withSale ? (
              <span className={styles.discount}>
                -{getDiscount(price, originPrice)}% off
              </span>
            ) : null}
          </h5>
          <p className={styles.perCoin}>
            ~ ${getPerCoinValue(price, credits)} per coin
          </p>
        </div>
        <HorizontalDivider />
        <div className={styles.info__features}>
          <FeatureBlock
            credits={credits}
            stickers={stickers}
            photos={photos}
            videos={videos}
            virtualGifts={virtualGifts}
          />
        </div>
      </div>
      <div className={styles.btn}>
        <Button
          onClick={() =>
            onClickHandler({
              videos,
              virtualGifts,
              photos,
              credits,
              stickers,
              id,
              name,
              price,
              originPrice
            })
          }
          fullwidth
          styleType='primary'
        >
          {withSale ? (
            <span className={styles.standardCost}>${originPrice}</span>
          ) : null}
          &nbsp;${price} Buy
        </Button>
      </div>
    </div>
  )
}

const PackageFirstCard: React.FC<PackageCardProps> = ({
  id,
  name,
  credits,
  stickers,
  photos,
  videos,
  virtualGifts,
  price,
  originPrice,
  onClickHandler
}) => {
  const withSale = originPrice && originPrice !== price
  const is768p = useMediaQuery(`(max-width: ${styles.is768p})`)

  return (
    <div className={styles.firstCard}>
      <div className={styles.firstCardInfo}>
        <div className={styles.firstCardHeader}>
          <h5 className={styles.firstCardName}>
            {name}
            {withSale ? (
              <span className={styles.firstCardDiscount}>
                -{getDiscount(price, originPrice)}% off
              </span>
            ) : null}
          </h5>
          {is768p ? (
            <p className={styles.firstCardPerCoin}>
              ~ ${getPerCoinValue(price, credits)} per coin
            </p>
          ) : null}
        </div>
        <div className={styles.firstCardFeatures}>
          {is768p ? (
            <FeatureItem icon={<GoCoin20 />} value={credits} name='GoCoins' />
          ) : (
            <>
              <AdvantageItem
                icon={<GoCoin20 />}
                title={`Free GoCoins x${credits}`}
                description='These coins can be spent on socializing, gifts, stickers, etc.'
              />
              <AdvantageItem
                icon={<Heart />}
                title='Get x3 super matches'
                description='Our system will select for you profiles with the highest possible similarity.'
              />
              <AdvantageItem
                icon={<Upload />}
                title='Profile search promotion'
                description='Your profile will appear higher than those with the free package.'
              />
              <AdvantageItem
                icon={<Incognito />}
                title='Browse Incognito'
                description={`You'll be able to browse profiles you're interested in and they won't know it.`}
                isNew
              />
              <AdvantageItem
                icon={<Headset />}
                title='Premium support'
                description='Get instant answers and help 24/7.'
              />
              <AdvantageItem
                icon={<HistoryCleaning />}
                title='Auto History Cleaning'
                description='We will automatically delete old messages'
              />
              <AdvantageItem
                icon={<MessageImage />}
                title='Live chatting'
                description='Send photos, videos, and gifts in chats.'
              />
              <AdvantageItem
                icon={<Messages />}
                title='Top in Messenger'
                description='Appear at the top of conversations.'
              />
            </>
          )}
        </div>
      </div>
      <Button
        onClick={() =>
          onClickHandler({
            videos,
            virtualGifts,
            photos,
            credits,
            stickers,
            id,
            name,
            price,
            originPrice
          })
        }
        styleType='primary'
        className={styles.firstCardBtn}
      >
        {withSale ? (
          <span className={styles.firstCardStandardCost}>${originPrice}</span>
        ) : null}
        &nbsp;${price} Buy
      </Button>
    </div>
  )
}
